import React, { SyntheticEvent, useEffect, useState } from 'react';
import { useProjectWorkers } from '../../hooks/useProjectWorkers';
import {
  Autocomplete,
  CircularProgress,
  Stack,
  TextField,
} from '@mui/material';
import { ProjectWorker } from '../../api';

interface Props {
  label?: string;
  onChangeSelectedId: (selectedId: string | undefined) => void;
  id?: string;
  isRequired: boolean;
}

interface Value {
  label: string;
  id: string;
}

export const PeopleSelector: React.FC<Props> = (props) => {
  const { onChangeSelectedId, id, label, isRequired } = props;
  const [value, setValue] = useState<Value | undefined | null>(null);

  const [options, setOptions] = useState<Record<string, ProjectWorker>>({});

  const { workers, isFetching } = useProjectWorkers();

  useEffect(() => {
    if (!workers) {
      return;
    }

    const opts: Record<string, ProjectWorker> = workers.reduce(
      (prev: Record<string, ProjectWorker>, curr: ProjectWorker) => {
        const { id } = curr;
        prev[id] = curr;

        return prev;
      },
      {} as Record<string, ProjectWorker>,
    );
    setOptions(opts);
  }, [workers.length]);

  const handleChange = (_: SyntheticEvent, value?: Value | null) => {
    if (!value?.id) {
      onChangeSelectedId(undefined);

      return;
    }

    const user = options[value.id];

    if (user) {
      onChangeSelectedId(user.id);
    }
  };

  useEffect(() => {
    if (!id) {
      setValue(null);

      return;
    }

    const option = Object.entries(options).find(
      ([optUid, _]) => optUid === id,
    );

    if (!option) {
      setValue(null);

      return;
    }

    const [_, worker] = option;

    setValue({ label: worker.name, id });
  }, [id, options]);

  if (isFetching) {
    return (
      <Stack alignItems="center">
        <CircularProgress />
      </Stack>
    );
  }

  return (
    <Autocomplete
      disablePortal
      options={Object.values(options)
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((worker) => ({ label: worker.name, id: worker.id }))}
      sx={{ width: 480 }}
      onChange={handleChange}
      renderOption={(props, option) => {
        return (
          <li {...props} key={option.id}>
            {option.label}
          </li>
        );
      }}
      renderInput={(params) => <TextField
        {...params}
        required={isRequired}
        label={label}
        placeholder={label}
      />}
      value={value}
      isOptionEqualToValue={(option?: Value, value?: Value) => {
        if (!option?.id && !value?.id) {
          return true;
        }

        if (!option?.id || !value?.id) {
          return false;
        }

        return option.id === value.id;
      }}
    />
  );
};
